import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NatureSelect from './child/NatureSelect';
import { priorityMeta } from '../utils/priority';

const ComplainFormPage = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Define formatDateForInput before useState
  const formatDateForInput = (date) => {
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [authUserId, setAuthUserId] = useState("");
  const [formData, setFormData] = useState({
    dept_id: "",
    district_id: "",
    complaint_nature_id: "",
    complaint_nearby_place: "",
    complaint_detail: "",
    complainant_name: "",
    complainant_address: "",
    complainant_phone: "",
    complainant_email: "",
    complaint_time: formatDateForInput(new Date()),
    complaint_additional_remarks: "",
    complainant_phone_secondry: "",
    other_nature: "",
    received_by: "",
    status: "Open",
    priority_level: "",
    cnic: "",
    complaint_source: "",
    complaint_reffered_by: "",
    complainant_tehsil: "",
    complainant_district: "",
    complaint_tehsil: "",
    complaint_district: "",
    complaint_address: ""
  });

  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [complainantTehsils, setComplainantTehsils] = useState([]); // Separate state for Complainant Tehsils
  const [complaintTehsils, setComplaintTehsils] = useState([]); // Separate state for Complaint Tehsils
  const [complaintNatures, setComplaintNatures] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [anonymize, setAnonymize] = useState(false);
  // "Informative call" flag — recorded for the record only, never dispatched.
  const [informative, setInformative] = useState(false);

  const complaintSources = [
    { value: "Helpline", label: "Helpline" },
    { value: "Chatbot", label: "Chatbot" },
    { value: "Whatsapp", label: "Whatsapp" },
    { value: "Social Media", label: "Social Media" },
    { value: "By Post", label: "By Post" },
    { value: "By Hand", label: "By Hand" },
    { value: "By Email", label: "By Email" },
    { value: "Other", label: "Other" }
  ];

  const complaintReferredBy = [
    { value: "Chief Minister's Secretariat", label: "Chief Minister's Secretariat" },
    { value: "Chief Secretariat Office", label: "Chief Secretariat Office" },
    { value: "Administrative Dept (Secretariat)", label: "Administrative Dept (Secretariat)" },
    { value: "Attached Department", label: "Attached Department" },
    { value: "Control Room (Helpline)", label: "Control Room (Helpline)" },
    { value: "Chief Conservator of Forests (PM&E)", label: "Chief Conservator of Forests (PM&E)" },
    { value: "Chief Conservator of Forests (Central)", label: "Chief Conservator of Forests (Central)" },
    { value: "Chief Conservator of Forests (Northern)", label: "Chief Conservator of Forests (Northern)" },
    { value: "Chief Conservator of Forests (Southern)", label: "Chief Conservator of Forests (Southern)" },
    { value: "Secretary Forest", label: "Secretary Forest" },
    { value: "AS PM&E", label: "AS PM&E" },
    { value: "Forest Social Media Team", label: "Forest Social Media Team" },
    { value: "Other", label: "Other" }
  ];

  // Fetch auth user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user._id) {
      setAuthUserId(user._id);
      setFormData(prev => ({ ...prev, received_by: user._id }));
    } else {
      console.error("User not found in localStorage");
    }
  }, []);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/departments/get-all-departments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDepartments(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch departments.");
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, [API_BASE_URL]);

  // Fetch Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/districts/get-all-districts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDistricts(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch districts.");
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistricts();
  }, [API_BASE_URL]);


  // Fetch Tehsils for Complainant District
  useEffect(() => {
    const fetchTehsils = async () => {
      if (formData.complainant_district) {
        try {
          const token = localStorage.getItem("token");
          const selectedDistrict = districts.find(d => d._id === formData.complainant_district);
          if (!selectedDistrict) {
            setComplainantTehsils([]);
            return;
          }
          const response = await axios.post(
            `${API_BASE_URL}/backend/districts/get-tehsils-from-district`,
            { district: selectedDistrict.name },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          setComplainantTehsils(response.data.tehsils || []);
          setFormData(prev => ({ ...prev, complainant_tehsil: "" }));
        } catch (error) {
          toast.error("Failed to fetch tehsils for the complainant district.");
          console.error("Error fetching complainant tehsils:", error);
          setComplainantTehsils([]);
        }
      } else {
        setComplainantTehsils([]);
        setFormData(prev => ({ ...prev, complainant_tehsil: "" }));
      }
    };
    fetchTehsils();
  }, [formData.complainant_district, districts, API_BASE_URL]);

  // Fetch Tehsils for Complaint District
  useEffect(() => {
    const fetchTehsils = async () => {
      if (formData.complaint_district) {
        try {
          const token = localStorage.getItem("token");
          const selectedDistrict = districts.find(d => d._id === formData.complaint_district);
          if (!selectedDistrict) {
            setComplaintTehsils([]);
            return;
          }
          const response = await axios.post(
            `${API_BASE_URL}/backend/districts/get-tehsils-from-district`,
            { district: selectedDistrict.name },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          setComplaintTehsils(response.data.tehsils || []);
          setFormData(prev => ({ ...prev, complaint_tehsil: "" }));
        } catch (error) {
          toast.error("Failed to fetch tehsils for the complaint district.");
          console.error("Error fetching complaint tehsils:", error);
          setComplaintTehsils([]);
        }
      } else {
        setComplaintTehsils([]);
        setFormData(prev => ({ ...prev, complaint_tehsil: "" }));
      }
    };
    fetchTehsils();
  }, [formData.complaint_district, districts, API_BASE_URL]);

  // Fetch Complaint Natures
  useEffect(() => {
    const fetchComplaintNatures = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/complaint-natures/get-all-complaint-natures`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setComplaintNatures(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch complaint natures.");
        console.error("Error fetching complaint natures:", error);
      }
    };
    fetchComplaintNatures();
  }, [API_BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "cnic") {
      let formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 5) {
        formattedValue = formattedValue.substring(0, 5) + '-' + formattedValue.substring(5);
      }
      if (formattedValue.length > 13) {
        formattedValue = formattedValue.substring(0, 13) + '-' + formattedValue.substring(13);
      }
      formattedValue = formattedValue.substring(0, 15);
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const cnicRegex = /^[0-9]{5}-[0-9]{7}-[0-9]{1}$/;
    if (name === "cnic" && value && !cnicRegex.test(value)) {
      toast.error("Please enter a valid CNIC in the format 12345-6789012-3");
    }
    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if ((name === "complainant_phone" || name === "complainant_phone_secondry") && value && !pakPhoneRegex.test(value)) {
      toast.error("Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)");
    }
  };

  const handleSelectChange = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "complaint_nature_id") {
      const selectedNature = complaintNatures.find(nature => nature._id === value);
      if (selectedNature && selectedNature.name.toLowerCase() === "other") {
        setShowOtherInput(true);
      } else {
        setShowOtherInput(false);
        setFormData(prev => ({ ...prev, other_nature: "" }));
      }
    }
  };

  // Nature drives priority: selecting a nature sets both the nature id AND the
  // (read-only) priority derived from that nature. The operator never picks a
  // priority directly.
  const handleNatureChange = (natureId) => {
    const selectedNature = complaintNatures.find((n) => n._id === natureId);
    const isOther = selectedNature && selectedNature.name?.toLowerCase() === "other";
    setShowOtherInput(!!isOther);
    setFormData((prev) => ({
      ...prev,
      complaint_nature_id: natureId,
      priority_level: selectedNature?.priority_level || "Medium",
      ...(isOther ? {} : { other_nature: "" }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentDateTime = new Date().toISOString();
    setFormData((prevData) => ({
      ...prevData,
      complaint_time: currentDateTime,
    }));

    const requiredFields = [
      "dept_id",
      "complainant_district",
      "complainant_tehsil",
      "complaint_nature_id",
      "complaint_nearby_place",
      "complaint_detail",
      "complainant_name",
      "complainant_address",
      "complainant_phone",
      "cnic",
      "complaint_source"
    ];

    const emptyFields = requiredFields.filter((field) => formData[field] === "");
    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.join(", ");
      toast.error(`Please fill in the following required details: ${fieldNames}`);
      return;
    }

    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if (!pakPhoneRegex.test(formData.complainant_phone)) {
      toast.error("Please enter a valid Primary phone number (e.g., +923001234567 or 03001234567)");
      return;
    }

    if (formData.complainant_phone_secondry && !pakPhoneRegex.test(formData.complainant_phone_secondry)) {
      toast.error("Please enter a valid Secondary phone number (e.g., +923001234567 or 03001234567)");
      return;
    }

    if (formData.complainant_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.complainant_email)) {
      toast.error("Invalid email format!");
      return;
    }

    const apiData = {
      complainant_name: formData.complainant_name,
      complainant_address: formData.complainant_address,
      complainant_contact_no: formData.complainant_phone,
      complainant_contact_no_sec: formData.complainant_phone_secondry,
      time_of_call: formData.complaint_time,
      complaint_detail: formData.complaint_detail,
      complainant_email: formData.complainant_email,
      dept_id: formData.dept_id,
      complaint_nature_id: formData.complaint_nature_id,
      near_by_place: formData.complaint_nearby_place,
      remarks: formData.complaint_additional_remarks,
      received_by: authUserId,
      // Informative calls are not part of the status lifecycle — they are
      // Resolved immediately (backend enforces this too).
      status: informative ? "Resolved" : "Open",
      priority_level: formData.priority_level,
      complainant_cnic: formData.cnic,
      complaint_source: formData.complaint_source,
      complaint_reffered_by: formData.complaint_reffered_by,
      ...(showOtherInput && { other_nature: formData.other_nature }),
      complainant_tehsil_id: formData.complainant_tehsil,
      complainant_district_id: formData.complainant_district,
      complaint_tehsil_id: formData.complaint_tehsil,
      complaint_district_id: formData.complaint_district,
      anonymize: anonymize,
      // Informative call: recorded for the record only, never dispatched to a
      // forest zone. Hidden from dashboards/counts/lists/reports; shown on the
      // dedicated Informative Calls page. Set via the checkbox below.
      informative: informative,
      complaint_address: formData.complaint_address
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_BASE_URL}/backend/complaints/create-complaint`, apiData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status) {
        try {
          const responseData = response.data.data;
          const complaintNumber = responseData.complaint_number;
          const contactNo = responseData.complainant_contact_no;

          // SMS to Complainant
          if (contactNo) {
            const smsMessage = `Respected Citizen, Your complaint(${complaintNumber}) has been successfully submitted to the Control Room of Forestry and Wildlife Department. Our team will review the matter and take appropriate action accordingly. Thank you for bringing this to our attention. Regards, 1084-Control Room.`;

            await axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
              params: {
                sec_key: process.env.REACT_APP_PITB_KEY,
                sms_text: smsMessage,
                phone_no: contactNo,
                sms_language: 'english'
              }
            });
          }

          // SMS to Admin
          const natureObj = complaintNatures.find(n => n._id === formData.complaint_nature_id);
          const districtObj = districts.find(d => d._id === formData.complaint_district);
          const natureName = natureObj ? natureObj.name : 'Unknown Consumer Nature';
          const districtName = districtObj ? districtObj.name : 'Unknown District';

          const adminSmsMessage = `Respected Sir, A Complaint No.${complaintNumber} has been successfully submitted by Complainant Name: ${responseData.complainant_name}, Contact number:${contactNo}, regarding ${natureName} in District:${districtName}. The complaint has been registered and is now ready for dispatch to the concerned officer for further necessary action.`;

          await axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
              params: {
                sec_key: process.env.REACT_APP_PITB_KEY,
                sms_text: adminSmsMessage,
                phone_no: "03379926560",
                sms_language: 'english'
              }
            });

        } catch (smsError) {
          console.log("Error sending SMS:", smsError);
        }

        toast.success("Complaint Submitted Successfully!");
        setFormData({
          dept_id: "",
          district_id: "",
          complaint_nature_id: "",
          complaint_nearby_place: "",
          complaint_detail: "",
          complainant_name: "",
          complainant_address: "",
          complainant_phone: "",
          complainant_email: "",
          complaint_time: formatDateForInput(new Date()),
          complaint_additional_remarks: "",
          complainant_phone_secondry: "",
          received_by: authUserId,
          status: "Open",
          priority_level: "",
          cnic: "",
          complaint_source: "",
          complaint_reffered_by: "",
          other_nature: "",
          complainant_tehsil: "",
          complainant_district: "",
          complaint_tehsil: "",
          complaint_district: "",
          complaint_address: ""
        });
        setComplainantTehsils([]);
        setComplaintTehsils([]);
        setInformative(false);
      }
    } catch (error) {
      toast.error("Failed to submit complaint. Please try again.");
      console.error("Error submitting complaint:", error);
    }
  };

  return (
    <div className="complaint-form col-lg-12 col-md-12 col-sm-12">
      <ToastContainer />
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-900 to-emerald-800 p-8 shadow-2xl shadow-emerald-900/40">
              <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-blue-400/10 blur-2xl"></div>
              <div className="relative z-10">
                {/* <div className="mb-2 flex items-center">
                  <span className="mr-3 h-1.5 w-8 rounded-full bg-amber-300"></span>
                  <span className="text-sm font-semibold uppercase tracking-widest text-success-300">
                    Department of Forestry & Wildlife
                  </span>
                </div> */}
                <h1 className="text-5xl font-bold leading-tight text-white">
                  <span className="text-3xl font-medium text-emerald-100 text-success-500">
                    Complaint Management Form
                  </span>
                </h1>
                <p className="text-lg italic text-emerald-100/90 text-success-300">
                  "Protecting our forests & wildlife"
                </p>
              </div>
            </div>
            <div className="">
              <div className="text-success-500 text-lg success-border-left">Complainant Information</div>
              <hr className="custom-hr"/>
              <div className="row gy-3">
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:office-building" className="text-lg" />
                    Department
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="dept_id"
                    value={formData.dept_id}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:account" className="text-lg" />
                    Complainant Name
                  </label>
                  <input
                    type="text"
                    name="complainant_name"
                    placeholder="Complainant Name"
                    className="form-control cpf-bg"
                    value={formData.complainant_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:phone" className="text-lg" />
                    Complainant Phone
                  </label>
                  <input
                    type="text"
                    name="complainant_phone"
                    placeholder="Phone Number (e.g., +923001234567 or 03001234567)"
                    className="form-control cpf-bg"
                    value={formData.complainant_phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="(\+92|0)3[0-9]{9}"
                    maxLength="13"
                    title="Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)"
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:phone" className="text-lg" />
                    Complainant Phone Secondary
                  </label>
                  <input
                    type="text"
                    name="complainant_phone_secondry"
                    placeholder="Phone Number Secondary (e.g., +923001234567 or 03001234567)"
                    className="form-control cpf-bg"
                    value={formData.complainant_phone_secondry}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="(\+92|0)3[0-9]{9}"
                    maxLength="13"
                    title="Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)"
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:card-account-details" className="text-lg" />
                    Complainant CNIC
                  </label>
                  <input
                    type="text"
                    name="cnic"
                    placeholder="CNIC (e.g., 12345-6789012-3)"
                    className="form-control cpf-bg"
                    value={formData.cnic}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="[0-9]{5}-[0-9]{7}-[0-9]{1}"
                    maxLength="15"
                    title="Please enter a valid CNIC in the format 12345-6789012-3"
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:email" className="text-lg" />
                    Complainant Email
                  </label>
                  <input
                    type="text"
                    name="complainant_email"
                    placeholder="info@gmail.com"
                    className="form-control cpf-bg"
                    value={formData.complainant_email}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:home" className="text-lg" />
                    Complainant Address
                  </label>
                  <input
                    type="text"
                    name="complainant_address"
                    placeholder="Complainant Address"
                    className="form-control cpf-bg"
                    value={formData.complainant_address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complainant District
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complainant_district"
                    value={formData.complainant_district}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select District</option>
                    {districts
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((district) => (
                        <option key={district._id} value={district._id}>
                          {district.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complainant Tehsil
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complainant_tehsil"
                    value={formData.complainant_tehsil}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select Tehsil</option>
                    {complainantTehsils
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((tehsil) => (
                        <option key={tehsil._id} value={tehsil._id}>
                          {tehsil.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="row my-3 mx-1">
                <div className="text-success-500 text-lg success-border-left mt-3">Complaint Information</div>
                <hr className="custom-hr"/>
              </div>
              <div className="row gy-3">
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:source" className="text-lg" />
                    Complaint Source
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complaint_source"
                    value={formData.complaint_source}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Source</option>
                    {complaintSources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:account-arrow-right" className="text-lg" />
                    Complaint Referred By
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complaint_reffered_by"
                    value={formData.complaint_reffered_by}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Reffered By</option>
                    {complaintReferredBy.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:alert-circle-outline" className="text-lg" />
                    Nature of Complaint
                  </label>
                  <NatureSelect
                    natures={complaintNatures}
                    value={formData.complaint_nature_id}
                    onChange={handleNatureChange}
                  />
                  {showOtherInput && (
                    <div className="mt-3">
                      <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                        <Icon icon="mdi:pencil-outline" className="text-lg" />
                        Specify Other Nature
                      </label>
                      <input
                        type="text"
                        className="form-control cpf-bg"
                        name="other_nature"
                        value={formData.other_nature}
                        onChange={handleInputChange}
                        placeholder="Please specify the nature of the complaint"
                      />
                    </div>
                  )}
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:priority-high" className="text-lg" />
                    Priority Level
                  </label>
                  {/* Read-only — derived from the selected nature of complaint. */}
                  <div
                    className="form-control cpf-bg d-flex align-items-center"
                    style={{ minHeight: 38 }}
                  >
                    {formData.priority_level ? (
                      (() => {
                        const m = priorityMeta(formData.priority_level);
                        return (
                          <span
                            className="d-inline-flex align-items-center gap-1"
                            style={{ background: m.color, color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}
                          >
                            <Icon icon={m.icon} /> {formData.priority_level}
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-muted" style={{ fontSize: 13 }}>Set automatically from the nature</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:home" className="text-lg" />
                    Complaint Address
                  </label>
                  <input
                    type="text"
                    name="complaint_address"
                    placeholder="Complaint Address"
                    className="form-control cpf-bg"
                    value={formData.complaint_address}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker-radius" className="text-lg" />
                    Nearby Landmark of Complainant
                  </label>
                  <input
                    type="text"
                    name="complaint_nearby_place"
                    placeholder="Nearby Landmark of Complainant"
                    className="form-control cpf-bg"
                    value={formData.complaint_nearby_place}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complaint District
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complaint_district"
                    value={formData.complaint_district}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select District</option>
                    {districts
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((district) => (
                        <option key={district._id} value={district._id}>
                          {district.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complaint Tehsil
                  </label>
                  <select
                    className="form-select cpf-bg"
                    name="complaint_tehsil"
                    value={formData.complaint_tehsil}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select Tehsil</option>
                    {complaintTehsils
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((tehsil) => (
                        <option key={tehsil._id} value={tehsil._id}>
                          {tehsil.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:clock" className="text-lg" />
                    Complaint Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="complaint_time"
                    className="form-control cpf-bg"
                    value={formData.complaint_time}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:file-document" className="text-lg" />
                    Complaint Detail
                  </label>
                  <textarea
                    name="complaint_detail"
                    className="form-control cpf-bg"
                    rows={4}
                    placeholder="Enter a description..."
                    value={formData.complaint_detail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:note-text" className="text-lg" />
                    Additional Remarks
                  </label>
                  <textarea
                    name="complaint_additional_remarks"
                    className="form-control cpf-bg"
                    rows={4}
                    placeholder="Additional Remarks..."
                    value={formData.complaint_additional_remarks}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-12 col-md-12 col-sm-12 my-3">
              <label
                className="form-label d-flex text-md text-white align-items-center gap-2"
                style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)' }}
              >
                <input
                  type="checkbox"
                  checked={anonymize}
                  onChange={(e) => setAnonymize(e.target.checked)}
                  className="form-check-input"
                />
                Please click here to keep Complainant information confidential during the process.
              </label>
            </div>
            <div className="col-lg-12 col-md-12 col-sm-12 my-3">
              <label
                className="form-label d-flex text-md text-white align-items-center gap-2"
                style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)' }}
              >
                <input
                  type="checkbox"
                  checked={informative}
                  onChange={(e) => setInformative(e.target.checked)}
                  className="form-check-input"
                />
                Mark as an Informative Call (recorded only — not dispatched to a forest zone, and hidden from dashboards, counts and reports).
              </label>
            </div>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-success text-sm btn-sm px-6 py-12 radius-12 my-24 mx-10 col-lg-2 col-md-4 col-sm-12"
                type="submit"
              >
                Submit Complaint
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplainFormPage;
